import React, { useCallback } from 'react';
import { useCrossFlow, Targets } from '@atlassiansox/cross-flow-support';
import Button from '@atlaskit/button/new';
import OldButton from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useBacklogSelector } from '../../../state/index.tsx';
import { getProjectName, projectIdSelector } from '../../../state/selectors/app/index.tsx';
import { getWhiteboardEligibility } from '../../../state/selectors/whiteboards-entrypoint-experiment/index.tsx';
import { useCoUseFlow } from '../co-use-flow/index.tsx';
import {
	WHITEBOARD_ENTRYPOINT_EXPERIMENT_VARIANTS,
	CTA_SOURCE_CONTEXT,
	CTA_SOURCE_COMPONENT,
} from '../constants.tsx';
import messages from '../messages.tsx';
import { onClickCTA } from '../on-click-cta/utils.tsx';

export const BacklogButtonVariant = () => {
	const { cohort, flow } = useBacklogSelector(getWhiteboardEligibility);

	const coUseEligible = flow === 'coUse';
	const crossFlowEligible = flow === 'crossFlow';
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const crossflow = useCrossFlow();
	const { fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();

	const url = useCoUseFlow(coUseEligible);

	// Extract project name and project id
	const projectName = useBacklogSelector(getProjectName);
	const projectId = useBacklogSelector(projectIdSelector);

	const onPlanOnWhiteboard = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			CTA_SOURCE_CONTEXT,
			{
				growthInitiative: 'cross-flow',
				isCrossFlowEligible: crossFlowEligible,
				isCoUseEligible: coUseEligible,
				sourceComponent: CTA_SOURCE_COMPONENT,
				sourceContext: CTA_SOURCE_CONTEXT,
			},
		);

		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: CTA_SOURCE_CONTEXT,
			},
			{
				targetProductId: Targets.CONFLUENCE,
				recommendedProductIds: [Targets.CONFLUENCE],
				source: CTA_SOURCE_COMPONENT,
			},
		);

		onClickCTA({
			createAnalyticsEvent,
			crossFlowEligible,
			crossflow,
			projectId,
			projectName,
			component: CTA_SOURCE_COMPONENT,
			context: CTA_SOURCE_CONTEXT,
			coUseEligible,
			url,
		});
	}, [
		crossFlowEligible,
		coUseEligible,
		projectId,
		projectName,
		crossflow,
		createAnalyticsEvent,
		fireInteractedUIEvent,
		url,
	]);

	if (!flow || cohort !== WHITEBOARD_ENTRYPOINT_EXPERIMENT_VARIANTS.BACKLOG_BUTTON) {
		return null;
	}

	return (
		<Box xcss={CTAContainer}>
			{isVisualRefreshEnabled() && fg('visual-refresh_drop_4') ? (
				<Button onClick={onPlanOnWhiteboard} spacing="compact">
					<Inline alignBlock="center" space="space.050">
						{formatMessage(messages.planOnWhiteboard)}
						{crossFlowEligible && (
							<Lozenge appearance="new">{formatMessage(messages.planOnWhiteboardTry)}</Lozenge>
						)}
					</Inline>
				</Button>
			) : (
				<OldButton onClick={onPlanOnWhiteboard}>
					<Inline alignBlock="center" space="space.050">
						{formatMessage(messages.planOnWhiteboard)}
						{crossFlowEligible && (
							<Lozenge appearance="new">{formatMessage(messages.planOnWhiteboardTry)}</Lozenge>
						)}
					</Inline>
				</OldButton>
			)}
		</Box>
	);
};

const CTAContainer = xcss({
	paddingLeft: 'space.100',
});
